import React, { useRef, useEffect, useState } from 'react';
import { debounce } from 'debounce';

import type {
  InterfaceBroadcastDimensions,
  InterfaceGenerateIframe,
  iframeOnLoad,
} from '../../@types/interfaces/GenerateIframe';
import { normalizeCssHeight } from '../normalizeCssHeight';

const broadcastDimensions = ({ width, height, contentWindow }: InterfaceBroadcastDimensions) => {
  if (contentWindow && width && height) {
    contentWindow.postMessage(
      {
        type: 'IFRAME_PARENT_DIMENSIONS',
        width,
        height,
      },
      '*',
    );
  }
};

const iframeOnLoad = ({
  src,
  trackingService,
  broadcastDimensionsArg,
  analytics,
}: iframeOnLoad) => {
  if (analytics && (analytics?.vendorName || analytics?.vendorType)) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'iframe_load',
          src,
          title: analytics?.vendorName || '',
          type: analytics?.vendorType || '',
        },
      },
    });
  }
  broadcastDimensions(broadcastDimensionsArg);
};

export const GenerateIframe = ({
  iframeProps,
  trackingService,
  presetSizing,
  presetIframe,
  analytics,
}: InterfaceGenerateIframe) => {
  const isSSR = typeof window === 'undefined';
  const [onFirstRender, setOnFirstRender] = useState(true);
  useEffect(() => {
    setOnFirstRender(false);
  }, []);

  const refIframe = useRef<HTMLIFrameElement>(null);
  const [iframeReady, setIframeReady] = useState(false);

  useEffect(() => {
    if (refIframe.current && !iframeReady) {
      setIframeReady(true);
    }
  }, [onFirstRender, iframeReady]);

  useEffect(() => {
    const iframe = refIframe.current;
    if (!iframe || !iframeReady) return undefined;

    let cleanup: (() => void) | undefined;

    const applyHeight = (height: number | string) => {
      if (height) {
        iframe.style.height = `${height}`;
        iframe.style.overflow = 'hidden';
        iframe.style.aspectRatio = 'unset';
        iframe.style.minHeight = 'unset';
        iframe.style.maxHeight = 'unset';
      }
    };

    const handleMessage = (event: MessageEvent) => {
      if (!iframe || event.source !== iframe.contentWindow) return;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      let { data } = event;

      if (typeof data === 'string') {
        try {
          data = JSON.parse(data) as unknown;
        } catch {
          return;
        }
      }

      if (!data || typeof data !== 'object') return;

      const dataObj = data as { height?: unknown; task?: { param?: { height?: unknown } } };
      const heightValue = dataObj.height ?? dataObj.task?.param?.height;
      const normalized = normalizeCssHeight(heightValue);

      if (normalized) {
        applyHeight(normalized);
      } else {
        console.warn('Invalid height value:', heightValue);
      }
    };

    const handleIframeLoad = () => {
      window.addEventListener('message', handleMessage, false);
      cleanup = () => {
        window.removeEventListener('message', handleMessage, false);
      };
    };

    iframe.addEventListener('load', handleIframeLoad);
    handleIframeLoad();

    return () => {
      iframe.removeEventListener('load', handleIframeLoad);
      if (cleanup) cleanup();
    };
  }, [iframeReady]);

  const broadcastDimensionsArg = {
    contentWindow: refIframe?.current?.contentWindow,
    width: refIframe?.current?.offsetWidth,
    height: refIframe?.current?.offsetHeight,
  };
  const listenerResize = debounce(() => {
    broadcastDimensions(broadcastDimensionsArg);
  }, 500);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      broadcastDimensions(broadcastDimensionsArg);

      if (presetSizing === 1) {
        window.addEventListener('resize', listenerResize);
      } else {
        window.removeEventListener('resize', listenerResize);
      }
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', listenerResize);
      }
    };
  }, [presetSizing]);

  useEffect(() => {
    if (
      presetIframe === 1 &&
      trackingService.featureAppName &&
      analytics &&
      (analytics?.vendorName || analytics?.vendorType)
    ) {
      trackingService.track({
        event: {
          eventInfo: {
            eventAction: 'iframe_load',
            src: iframeProps.src || '',
            title: analytics?.vendorName || '',
            type: analytics?.vendorType || '',
          },
        },
      });
    }
  }, [trackingService.featureAppName]);

  const modifiedIframeProps = {
    ...iframeProps,
    ref: refIframe,
    onLoad: (event: React.SyntheticEvent<HTMLIFrameElement>) => {
      iframeOnLoad({
        src: event.currentTarget.src,
        trackingService,
        analytics,
        broadcastDimensionsArg,
      });
    },
  } as React.DetailedHTMLProps<React.IframeHTMLAttributes<HTMLIFrameElement>, HTMLIFrameElement>;
  switch (presetIframe) {
    case 1:
      return (
        <iframe {...modifiedIframeProps} onLoad={() => {}} title={modifiedIframeProps.title} />
      );
    case 2:
    case 3:
      return isSSR || onFirstRender ? null : (
        <iframe {...modifiedIframeProps} title={modifiedIframeProps.title} />
      );
    case 4:
      return isSSR || onFirstRender ? null : (
        <iframe
          {...modifiedIframeProps}
          title={modifiedIframeProps.title}
          src={undefined}
          srcDoc={iframeProps.srcDoc}
          onLoad={() => {}}
        />
      );
    default:
      return null;
  }
};
